// ============================================================================
// Location Utility Functions
// Extracted from AppointmentForm.js — pure functions, no component state.
// ============================================================================

import cityCoordinates from '../cityCoordinates';
import { LOCATIONS_WITH_COORDS, DEBUG_MODE } from './constants';

// ---------------------------------------------------------------------------
// Haversine distance (miles between two lat/lon points)
// ---------------------------------------------------------------------------
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ---------------------------------------------------------------------------
// Get clinics sorted by proximity to the patient's zipcode.
//
// Dependencies are injected so this function has no state coupling:
//   lookupZipcodeFn  — async (zipcode: string) => zipData array
//   addToDebugLogFn  — (action, data, response, error) => void  (optional)
// ---------------------------------------------------------------------------
export async function getNearestLocations(patientZipcode, lookupZipcodeFn, addToDebugLogFn = () => {}) {
  try {
    const zipData = await lookupZipcodeFn(patientZipcode);

    if (!zipData || zipData.length === 0) {
      if (DEBUG_MODE) {
        console.warn('Could not find coordinates for zipcode:', patientZipcode);
        console.warn('📍 Returning only virtual options (Homepsych)');
      }
      return Object.values(LOCATIONS_WITH_COORDS).filter(loc => loc.isVirtual);
    }

    let patientLat = zipData[0].Latitude;
    let patientLon = zipData[0].Longitude;

    // Fallback: use city-based approximation when the API omits coordinates
    if (!patientLat || !patientLon) {
      const cityName = zipData[0].City;
      if (DEBUG_MODE) {
        console.warn('⚠️ No coordinates in API response for zipcode:', patientZipcode);
        console.log('📍 Attempting to use city-based coordinates for:', cityName);
      }

      if (cityName && cityCoordinates[cityName]) {
        patientLat = cityCoordinates[cityName].lat;
        patientLon = cityCoordinates[cityName].lon;
        if (DEBUG_MODE) {
          console.log('✅ Using coordinates for', cityName, ':', { lat: patientLat, lon: patientLon });
        }
      } else {
        if (DEBUG_MODE) {
          console.warn('❌ No coordinates available for city:', cityName);
          console.warn('📍 Returning only virtual options (Homepsych)');
        }
        return Object.values(LOCATIONS_WITH_COORDS).filter(loc => loc.isVirtual);
      }
    }

    const locationsWithDistance = Object.values(LOCATIONS_WITH_COORDS)
      .map(location => {
        if (location.isVirtual) {
          return { ...location, distance: 0, isVirtual: true };
        }
        const distance = calculateDistance(patientLat, patientLon, location.lat, location.lon);
        return { ...location, distance };
      })
      .sort((a, b) => {
        if (a.isVirtual) return -1;
        if (b.isVirtual) return 1;
        return a.distance - b.distance;
      });

    const filteredLocations = locationsWithDistance.filter(
      loc => loc.isVirtual || loc.distance <= 30
    );

    const hasNearbyLocations = filteredLocations.some(loc => !loc.isVirtual);

    if (hasNearbyLocations) {
      const closestPhysical = filteredLocations.find(loc => !loc.isVirtual);
      if (closestPhysical) {
        closestPhysical.isClosest = true;
      }
    }

    if (!hasNearbyLocations) {
      const virtualOnly = filteredLocations.filter(loc => loc.isVirtual);
      if (DEBUG_MODE) {
        console.log('🌍 [LOCATION FILTER] No nearby physical locations - returning only virtual options');
      }
      return virtualOnly;
    }

    if (DEBUG_MODE) {
      console.log('🌍 [LOCATION FILTER] Patient Zipcode:', patientZipcode);
      console.log('🌍 [LOCATION FILTER] Patient Coords:', { lat: patientLat, lon: patientLon });
      console.log('🌍 [LOCATION FILTER] Locations with distances:', locationsWithDistance);
      console.log('🌍 [LOCATION FILTER] Filtered (≤30 miles):', filteredLocations);
      console.log('🌍 [LOCATION FILTER] Has nearby locations:', hasNearbyLocations);
    }

    addToDebugLogFn('Nearest Locations Calculated', {
      patientZipcode,
      patientCoords: { lat: patientLat, lon: patientLon },
      totalLocations: locationsWithDistance.length,
      nearbyLocations: filteredLocations.length,
      hasNearbyLocations,
      onlyVirtualAvailable: !hasNearbyLocations
    }, filteredLocations, null);

    return filteredLocations;
  } catch (error) {
    if (DEBUG_MODE) {
      console.error('Error calculating nearest locations:', error);
      console.warn('📍 Returning only virtual options (Homepsych)');
    }
    return Object.values(LOCATIONS_WITH_COORDS).filter(loc => loc.isVirtual);
  }
}

// ---------------------------------------------------------------------------
// Map a facility ID to a short display name.
// Note: '215' (Hollister) was missing from the original map — added here.
// ---------------------------------------------------------------------------
export function getLocationName(facilityId) {
  const locationMap = {
    '199': 'Redding',
    '210': 'Eureka',
    '211': 'Fairfield',
    '212': 'Homepsych',
    '214': 'Red Bluff',
    '215': 'Hollister',
    '216': 'Monterey',
    '217': 'Salinas'
  };
  return locationMap[facilityId] || 'Unknown Location';
}
